import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { take } from 'lib/array/arrayUtils'
import { rem } from 'polished'
import Heading from 'LuxKit/Typography/Heading'
import { useInView } from 'react-intersection-observer'
import { fadeIn } from 'components/utils/animations'
import useMountedEffect from 'hooks/useMountedEffect'
import CalendarDaysMonth from './CalendarDaysMonth'
import CalendarPriceBlurb from './CalendarPriceBlurb'
import { INTL_LONG_MONTH_LONG_YEAR } from 'constants/dateFormats'
import { getDateFromMonthKey, useIntlDateFormatter } from 'lib/datetime/dateUtils'

const Root = styled.div`
  width: 100%;
`

const Calendar = styled.div`
  animation: ${fadeIn} 0.2s;

  & + & {
    margin-top: ${rem(40)};
  }

  > * + * {
    margin-top: ${rem(20)};
  }
`

interface Props {
  className?: string
  onDatesChange: (day: App.BedbankCalendarDay) => void
  monthIndex: string
  duration: number
  fetching: boolean
  flights: boolean
  daysByMonth: Array<App.BedbankCalendarMonth>
  contentRef: React.RefObject<HTMLDivElement | null>
  adultsNumber: number
  roomsNumber: number
  displayPricingAsPerPerson: boolean
}

function CalendarDayPicker(props: Props) {
  const {
    className,
    monthIndex,
    daysByMonth,
    contentRef,
    onDatesChange,
    duration,
    fetching,
    flights,
    adultsNumber,
    roomsNumber,
    displayPricingAsPerPerson,
  } = props
  const dateFormatter = useIntlDateFormatter()
  // always show month selected, previous month and next month
  const initialMonthIndex = useRef(daysByMonth.findIndex((entry) => entry.key === monthIndex)).current
  const [monthsOnDisplay, setMonthsOnDisplay] = useState({ toShow: 3, offset: Math.max(initialMonthIndex - 1, 0) })
  const [previousRef, previousInView] = useInView({ root: contentRef.current, rootMargin: '20px 0px 0px 0px' })
  const [nextRef, nextInView] = useInView({ root: contentRef.current, rootMargin: '0px 0px 100px 0px' })
  const monthElements = useRef<Record<string, HTMLDivElement>>({})
  const scrollRestoreMonth = useRef<typeof monthsOnDisplay | null>(null)

  // We discard the initial 'previous in view' check because we already show
  // an extra month before this one on mount
  useMountedEffect(() => {
    if (previousInView) {
      scrollRestoreMonth.current = monthsOnDisplay
      setMonthsOnDisplay({ toShow: monthsOnDisplay.toShow + 1, offset: monthsOnDisplay.offset - 1 })
    }
  }, [previousInView])

  // mounting layout handles transition from month to day calendar
  useLayoutEffect(() => {
    if (initialMonthIndex > 0) {
      // we need to scroll to the 'middle' month
      const month = daysByMonth[monthsOnDisplay.offset]
      const scrollPosition = monthElements.current[month.key]?.offsetHeight + 40
      if (scrollPosition) {
        contentRef.current?.scrollTo({ behavior: 'auto', top: scrollPosition })
      }
    }
  }, []) // oxlint-disable-line react-hooks/exhaustive-deps

  // on offset change handles when scrolling up and new month appears
  useLayoutEffect(() => {
    if (scrollRestoreMonth.current !== undefined) {
      const month = daysByMonth[monthsOnDisplay.offset]
      const scroll = monthElements.current[month.key]?.offsetHeight + 40

      if (scroll) {
        contentRef.current?.scrollTo({ behavior: 'auto', top: scroll })
      }
      scrollRestoreMonth.current = null
    }
  }, [monthsOnDisplay.offset]) // oxlint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (nextInView) {
      setMonthsOnDisplay(monthsOnDisplay => ({ ...monthsOnDisplay, toShow: monthsOnDisplay.toShow + 1 }))
    }
  }, [nextInView])

  const keyData = flights ? 'cheapestDayWithFlights' : 'cheapestDay'

  return (
    <Root className={className}>
      {monthsOnDisplay.offset > 0 && <div ref={previousRef} />}
      {take(daysByMonth, monthsOnDisplay.toShow, monthsOnDisplay.offset).map((value) => {
        const monthName = dateFormatter(getDateFromMonthKey(value.key), INTL_LONG_MONTH_LONG_YEAR)

        return <Calendar
          key={value.key}
          ref={(ref) => {
            if (ref) {
              monthElements.current[value.key] = ref
            }
          }}
        >
          <div>
            <Heading variant="heading3">{monthName}</Heading>
            {!!value[keyData] && !fetching && <CalendarPriceBlurb
              price={value[keyData]?.price}
              propertyFees={value[keyData]?.propertyFees}
              flightPrice={value[keyData]?.flightPrice}
              displayPricingAsPerPerson={displayPricingAsPerPerson}
              adultsNumber={adultsNumber}
            />}
          </div>
          <CalendarDaysMonth
            days={value.days}
            onDatesChange={onDatesChange}
            duration={duration}
            flights={flights}
            displayPricingAsPerPerson={displayPricingAsPerPerson}
            adultsNumber={adultsNumber}
            roomsNumber={roomsNumber}
          />
        </Calendar>
      })}
      {(monthsOnDisplay.toShow + monthsOnDisplay.offset) < daysByMonth.length && <div ref={nextRef} />}
    </Root>
  )
}

export default CalendarDayPicker
